<template>
  <div class="form" :class="{ loading: formIsLoading }">
    <Result v-bind="$attrs" v-model:error="formError" v-model:is-success="formIsSuccess" />
    <div class="form-section">
      <div class="form-row">
        <input v-model="formLabel" type="text" placeholder="Label - eg. Amex Platinum" />
      </div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="formName" type="text" required placeholder="Name on card" />
        </div>
        <div class="form-col">
          <input v-model="formNumber" type="number" required placeholder="Card number" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-row form-col">
          <div class="form-col">
            <select v-model="formExpiryMonth" required>
              <option value="" disabled>Expiry Month</option>
              <option v-for="m in 12" :key="m" :value="m">
                {{ m < 10 ? "0" + m : m }}
              </option>
            </select>
          </div>
          <div class="form-col">
            <select v-model="formExpiryYear" required>
              <option value="" disabled>Expiry Year</option>
              <option v-for="y in formYears" :key="y" :value="y">
                {{ y }}
              </option>
            </select>
          </div>
        </div>
        <div class="form-col">
          <input v-model="formSecurity" type="text" required placeholder="Security code" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="formAddressStreet" type="text" required placeholder="Street address" />
        </div>
        <div class="form-col">
          <input v-model="formAddressCity" type="text" required placeholder="City" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-row form-col">
          <div class="form-col">
            <input v-model="formAddressState" type="text" required placeholder="State/Region" />
          </div>
          <div class="form-col">
            <input v-model="formAddressZip" type="text" required placeholder="Zip/Post code" />
          </div>
        </div>
        <div class="form-col">
          <select v-model="formAddressCountry" required>
            <option value="" disabled>Country</option>
            <option v-for="c in formCountries" :key="c.id" :value="c.title">
              {{ c.title }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <button @click="save">Save Card</button>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, inject } from "vue";
import Result from "../../components/common/FormResult.vue";
import useApi from "../../utils/apiCalls.js";

const props = defineProps({
  organizationId: {
    type: [Number, null],
    default: null,
  },
});

const emit = defineEmits(["error", "update:isNew"]);

const { apiGetRequest, apiPostRequest } = useApi(emit);

const userId = inject("userId");

const formCountries = ref([]);
const formLabel = ref(null);
const formName = ref(null);
const formNumber = ref(null);
const formExpiryMonth = ref("");
const formExpiryYear = ref("");
const formSecurity = ref(null);
const formAddressStreet = ref(null);
const formAddressState = ref(null);
const formAddressCity = ref(null);
const formAddressZip = ref(null);
const formAddressCountry = ref("");
const formIsLoading = ref(false);
const formIsSuccess = ref(false);
const formError = ref(null);

const formYears = computed(() => {
  const year = new Date().getFullYear();
  return Array.from({ length: 10 }, (_, index) => year + index);
});

watch(
  () => props.organizationId,
  () => {
    resetForm();
    loadCountries();
  }
);

onMounted(() => {
  loadCountries();
});

function resetForm() {
  formLabel.value = null;
  formName.value = null;
  formNumber.value = null;
  formExpiryMonth.value = "";
  formExpiryYear.value = "";
  formSecurity.value = null;
  formAddressStreet.value = null;
  formAddressState.value = null;
  formAddressCity.value = null;
  formAddressZip.value = null;
  formAddressCountry.value = "";
  formIsLoading.value = false;
  formIsSuccess.value = false;
  formError.value = null;
}

function loadCountries() {
  if (formCountries.value.length === 0) {
    formIsLoading.value = true;
    apiGetRequest({
      path: "/countries",
      onSuccess: data => {
        formCountries.value = data;
        formIsLoading.value = false;
      },
      onError: error => {
        formIsLoading.value = false;
        emit("error", error);
      },
    });
  }
}

function save() {
  formIsLoading.value = true;

  const url = props.organizationId ? `/organizations/${props.organizationId}/cards` : `/customers/${userId.value}/cards`;

  apiPostRequest({
    path: url,
    data: {
      number: formNumber.value ? formNumber.value.toString() : null,
      cvc: formSecurity.value ? formSecurity.value.toString() : null,
      expiry_month: formExpiryMonth.value ? formExpiryMonth.value.toString() : null,
      expiry_year: formExpiryYear.value ? formExpiryYear.value.toString() : null,
      full_name: formName.value,
      street: formAddressStreet.value,
      city: formAddressCity.value,
      state: formAddressState.value,
      label: formLabel.value,
      country: formAddressCountry.value ? formAddressCountry.value : null,
      zip_code: formAddressZip.value,
    },
    onSuccess: () => {
      resetForm();
      formIsLoading.value = false;
      formIsSuccess.value = true;
      emit("update:isNew", true);
    },
    onError: error => {
      formIsLoading.value = false;
      formIsSuccess.value = false;
      formError.value = error;
      emit("error", error);
    },
  });
}
</script>
